import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { DocumentsCard } from "../components/DocumentsCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Building2,
  Users,
  Clock,
  DollarSign,
  ArrowRight,
  Plus,
  TrendingUp,
  AlertCircle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorDashboard = () => {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, propertiesRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/vendor`),
        axios.get(`${API_URL}/api/projects`),
        axios.get(`${API_URL}/api/payments`),
      ]);
      setStats(dashboardRes.data);
      setProjects(propertiesRes.data.slice(0, 4));
      setRecentPayments(paymentsRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "badge-pending",
      processing: "bg-blue-100 text-blue-800 border border-blue-200",
      completed: "badge-completed",
      failed: "badge-failed",
      returned: "bg-orange-100 text-orange-800 border border-orange-200",
    };
    return variants[status] || "bg-stone-100 text-stone-800";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-stone-600 mt-1">Welcome back! Track your payments received from CPC.</p>
          </div>
          <Link to="/projects">
            <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-hover" data-testid="stat-projects">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600">Total Projects</p>
                  <p className="text-2xl font-bold text-stone-900 mt-1">{stats?.total_projects || 0}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-employees">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600">Active Employees</p>
                  <p className="text-2xl font-bold text-stone-900 mt-1">{stats?.total_employees || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600">Pending Payments</p>
                  <p className="text-2xl font-bold text-stone-900 mt-1">{stats?.pending_payments || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-600">This Month</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">
                    {formatCurrency(stats?.completed_this_month || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Projects */}
          <Card data-testid="projects-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Projects</CardTitle>
                <CardDescription>Your managed projects</CardDescription>
              </div>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-emerald-700">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-600 mb-4">No projects yet</p>
                  <Link to="/projects">
                    <Button size="sm" className="bg-emerald-900 hover:bg-emerald-800 rounded-full">
                      Add Your First Project
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-700" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{project.name}</p>
                          <p className="text-sm text-stone-500">{project.address}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-stone-900">{formatCurrency(project.rent_amount)}</p>
                        <p className="text-xs text-stone-500">
                          {project.employee_name || "No employee"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card data-testid="payments-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Payments</CardTitle>
                <CardDescription>Latest payment activity</CardDescription>
              </div>
              <Link to="/payments">
                <Button variant="ghost" size="sm" className="text-emerald-700">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentPayments.length === 0 ? (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-600">No payments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentPayments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-200 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{payment.property_name}</p>
                          <p className="text-sm text-stone-500 capitalize">{payment.payment_type.replace("_", " ")}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-stone-900">{formatCurrency(payment.amount)}</p>
                        <Badge className={`text-xs ${getStatusBadge(payment.status)}`}>
                          {payment.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Documents */}
        <DocumentsCard />

        {/* Upcoming Payments Alert */}
        {stats?.upcoming_payments?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="upcoming-payments-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">
                    {stats.upcoming_payments.length} pending payment{stats.upcoming_payments.length > 1 ? "s" : ""}
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Review and track upcoming payments from your employees.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default VendorDashboard;
